import * as React from "react";
import { ulid } from "ulid";
import { SidePanel as SharedSidePanel } from "@/components/chat/SidePanel";
import type { SidePanelTab, SidePanelTabConfig } from "@/components/chat/types";
import { SidebarPromptImprovements } from "@/modules/open-chat/components/sidebar/sidebarPromptImprovements";
import { PromptLibraryRightDrawer } from "@/modules/promptLibrary/components/promptLibraryRightDrawer";
import { PDFViewer } from "@/components/feature/pdf/pdfViewer";
import { RightDrawerDocumentName } from "@/components/feature/layout/right-sidebar/components/documentName";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { ReportPanelType } from "../types";

interface Props {
  activePanel: ReportPanelType;
  isPanelOpen: boolean;
  onPanelChange: (panel: Exclude<ReportPanelType, null>) => void;
  onClose: () => void;
  enabledTabs: ReadonlyArray<SidePanelTab>;
}

const PANEL_TO_TAB: Record<Exclude<ReportPanelType, null>, SidePanelTab> = {
  pdf: "pdf",
  improve: "improve",
  library: "library",
};

const TAB_TO_PANEL: Record<SidePanelTab, ReportPanelType> = {
  pdf: "pdf",
  artefacts: null,
  improve: "improve",
  library: "library",
  history: null,
  sources: null,
  files: null,
  snapshot: null,
};

const ALL_TABS: SidePanelTabConfig[] = [
  { id: "pdf", label: "PDF" },
  { id: "improve", label: "Improve" },
  { id: "library", label: "Library" },
];

export const SidePanel = ({ activePanel, isPanelOpen, onPanelChange, onClose, enabledTabs }: Props) => {
  const [promptGeneratorConversationId] = React.useState(() => ulid());
  const { currentPdfId, currentCitation } = useCurrentPdf();

  const tabs = React.useMemo(() => ALL_TABS.filter(t => enabledTabs.includes(t.id)), [enabledTabs]);

  const activeTab = activePanel ? PANEL_TO_TAB[activePanel] : null;

  const handleTabChange = (tab: SidePanelTab) => {
    const panel = TAB_TO_PANEL[tab];
    if (panel) {
      onPanelChange(panel);
    }
  };

  return (
    <SharedSidePanel
      activeTab={activeTab}
      isOpen={isPanelOpen}
      tabs={tabs}
      onTabChange={handleTabChange}
      onClose={onClose}
      widthClassName="w-1/2"
    >
      {activePanel === "pdf" && currentPdfId && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0">
            <RightDrawerDocumentName />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <PDFViewer citationHighlight={currentCitation} />
          </div>
        </div>
      )}
      {activePanel === "pdf" && !currentPdfId && (
        <div className="flex flex-1 items-center justify-center p-4 text-center text-sm text-muted-foreground">
          Select a citation in the report to view the source document.
        </div>
      )}
      {activePanel === "improve" && <SidebarPromptImprovements conversationId={promptGeneratorConversationId} />}
      {activePanel === "library" && <PromptLibraryRightDrawer source="qa" />}
    </SharedSidePanel>
  );
};
